import type React from 'react';

import * as styles from './styles';
import moment from 'moment';

import { getLicensePlateOrCobliIdOrPlaceholder, type TimestampInMillis, type UUID, type Vehicle } from '@cobliteam/common-types';

import { isVehicleDashcam } from '@cobliteam/api-lib';

import { CameraIdentifier } from '@cobliteam/media';

import {
  CaptionDefault,
  CaptionError,
  DescriptionListDefault,
  DescriptionListMedium,
  DescriptionTitle,
  NumberInput,
  PlaceholderText,
  SingleDatePicker,
} from '@cobliteam/base-components';

import { OdometerInfoTooltip } from '../../../OdometerInfoTooltip/OdometerInfoTooltip';

export interface VehiclesLastMaintenanceDate {
  [vehicleId: UUID]: TimestampInMillis | undefined;
}

export interface VehiclesLastOdometer {
  [vehicleId: UUID]: number | undefined;
}

export interface PastMaintenanceSetupProps {
  vehiclesToSetup: Vehicle[];
  vehiclesLastMaintenanceDate: VehiclesLastMaintenanceDate;
  vehiclesOdometer: VehiclesLastOdometer;
  vehiclesLastOdometer: VehiclesLastOdometer;
  onChangeVehiclesLastMaintenanceDate: (data: VehiclesLastMaintenanceDate) => void;
  onChangeVehiclesLastOdometer: (data: VehiclesLastOdometer) => void;
}

export const PastMaintenanceSetup: React.FC<PastMaintenanceSetupProps> = ({
  vehiclesToSetup,
  vehiclesLastMaintenanceDate,
  vehiclesOdometer,
  vehiclesLastOdometer,
  onChangeVehiclesLastMaintenanceDate,
  onChangeVehiclesLastOdometer,
}) => {
  const numberFormatter = Intl.NumberFormat('pt-BR');

  const handleLastMaintenanceDateChange = (vehicle: Vehicle, beginDayInMillis: number) => {
    onChangeVehiclesLastMaintenanceDate({
      ...vehiclesLastMaintenanceDate,
      [vehicle.id]: beginDayInMillis,
    });
  };

  const handleOdometerValueChange = (vehicle: Vehicle, newValue?: number) => {
    onChangeVehiclesLastOdometer({
      ...vehiclesLastOdometer,
      [vehicle.id]: newValue,
    });
  };

  const renderChild = (vehicle: Vehicle) => {
    const odometerInputValue = vehiclesOdometer[vehicle.id] || 0;
    const lastOdometerInputValue = vehiclesLastOdometer[vehicle.id];
    const lastMaintenanceDateValue = vehiclesLastMaintenanceDate[vehicle.id];

    const isMissingOdometerInput = !lastOdometerInputValue && lastMaintenanceDateValue;
    const hasInvalidLastOdometerInput = Boolean(lastOdometerInputValue && lastOdometerInputValue >= odometerInputValue);
    const odometerError = isMissingOdometerInput ? 'Preencha este campo' : hasInvalidLastOdometerInput ? 'Valor deve ser menor que o odômetro atual' : undefined;

    return (
      <tr className={styles.info} key={vehicle.id}>
        <td className={styles.iconsColumnData}>{isVehicleDashcam(vehicle) && <CameraIdentifier hasCta={false} />}</td>
        <td className={styles.vehicleInfo}>
          <DescriptionTitle ellipsis>{getLicensePlateOrCobliIdOrPlaceholder(vehicle)}</DescriptionTitle>
          <DescriptionListDefault ellipsis>
            {`${vehicle.model.brand || ''} ${vehicle.model.name || ''}`.trim() || <PlaceholderText>Marca Modelo</PlaceholderText>}
          </DescriptionListDefault>
        </td>
        <td className={styles.calendar}>
          <SingleDatePicker
            handleDayChange={(date) => handleLastMaintenanceDateChange(vehicle, date)}
            placeholder='Data da última manutenção'
            error={Boolean(lastOdometerInputValue && !lastMaintenanceDateValue)}
            errorMessage='Preencha este campo'
            selectedDateInMillis={lastMaintenanceDateValue}
            disabledDays={{ after: moment().toDate() }}
          />
        </td>
        <td className={styles.odometer}>
          <CaptionDefault>{`Odômetro atual: ${numberFormatter.format(odometerInputValue)} km`}</CaptionDefault>
          <NumberInput
            placeholder='Km na última manutenção'
            error={Boolean(odometerError)}
            className={styles.odometer}
            value={lastOdometerInputValue}
            onNumberChange={(value) => handleOdometerValueChange(vehicle, value)}
          />
          <CaptionError>{odometerError || ''}</CaptionError>
        </td>
      </tr>
    );
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <td className={styles.iconsColumn} />
          <td className={styles.vehicleColumn}>
            <DescriptionListMedium>Veículos</DescriptionListMedium>
          </td>
          <td colSpan={2}>
            <DescriptionListMedium>
              Última manutenção do veículo (opcional)
              <OdometerInfoTooltip title='Informações de odômetro' />
            </DescriptionListMedium>
          </td>
        </tr>
      </thead>
      <tbody>{vehiclesToSetup.map(renderChild)}</tbody>
    </table>
  );
};
